/**
 * The CPU opponent. A posture state-machine that emits the *same* Command a
 * human would (DESIGN §6). Difficulty scales reaction time and aim precision —
 * never the bey's stats. Pure: deterministic given an injected RNG.
 */
import { typeAdvantage, type BeyType } from './stats.ts';
import type { Command, Controller, WorldSnapshot } from './command.ts';
import { NO_COMMAND } from './command.ts';
import { isSpecialReady } from './specials.ts';
import type { Vec2 } from './vec.ts';
import { sub, add, scale, normalize, dist, len } from './vec.ts';

export type Posture = 'aggressive' | 'defensive' | 'evasive';

export interface AiConfig {
  /** 0 (sluggish, sloppy) .. 1 (instant, precise). */
  difficulty: number;
  /** Bey archetype, sets the default posture. */
  type: BeyType;
  rng?: () => number;
}

function defaultPosture(type: BeyType): Posture {
  switch (type) {
    case 'attack':
      return 'aggressive';
    case 'defense':
      return 'defensive';
    case 'stamina':
      return 'evasive';
    default:
      return 'aggressive';
  }
}

export class AiController implements Controller {
  private readonly difficulty: number;
  private readonly base: Posture;
  private readonly cfgType: BeyType;
  private readonly rng: () => number;

  /** Re-decide only every reactionTime seconds (cached command in between). */
  private readonly reactionTime: number;
  private timer = 0;
  private cached: Command = NO_COMMAND;

  constructor(cfg: AiConfig) {
    this.difficulty = Math.max(0, Math.min(1, cfg.difficulty));
    this.base = defaultPosture(cfg.type);
    this.cfgType = cfg.type;
    this.rng = cfg.rng ?? Math.random;
    // 0.32s reaction at easy, ~0.06s at hardest.
    this.reactionTime = 0.32 - 0.26 * this.difficulty;
  }

  decide(world: WorldSnapshot, dt: number): Command {
    this.timer -= dt;
    if (this.timer > 0) return this.cached;
    this.timer = this.reactionTime;
    this.cached = this.think(world);
    return this.cached;
  }

  /** Type-matchup multiplier for self vs. the opponent (>1 favourable). */
  private advantage(world: WorldSnapshot): number {
    return typeAdvantage(this.cfgType, world.opponent.type, 1.3, 0.8);
  }

  /** Choose the live posture from the situation, matchup, and spin position. */
  private posture(world: WorldSnapshot): Posture {
    const { self, opponent } = world;
    const spinRatio = self.spin / Math.max(1, opponent.spin);
    const adv = this.advantage(world);

    // Badly out-spun: attackers gamble on offense, everyone else survives.
    if (spinRatio < 0.55) return this.base === 'aggressive' ? 'aggressive' : 'evasive';
    // Sitting on a big lead (and not an attacker): run the clock to a spin win.
    if (spinRatio > 1.4 && this.base !== 'aggressive') return 'evasive';
    // Press a favourable matchup while healthy; play safe in a bad one.
    if (adv > 1 && spinRatio > 0.7) return 'aggressive';
    if (adv < 1 && this.base !== 'aggressive') return 'defensive';
    return this.base;
  }

  private think(world: WorldSnapshot): Command {
    const { self, opponent, arenaCenter, rimRadius } = world;
    const posture = this.posture(world);
    const toOpp = sub(opponent.pos, self.pos);
    const range = dist(self.pos, opponent.pos);
    const toCenter = sub(arenaCenter, self.pos);
    const selfFromCenter = len(sub(self.pos, arenaCenter));

    // Predict where the opponent will be (lead the target by difficulty).
    const lead = scale(opponent.vel, 0.18 * this.difficulty);
    const aimPoint = add(opponent.pos, lead);
    const aimDir = this.aimWithNoise(normalize(sub(aimPoint, self.pos)));

    let steer: Vec2;
    let dash = false;
    let extremeDash = false;
    let holdCenter = false;

    if (posture === 'aggressive') {
      // Gentle bias toward the opponent so the orbit curves into swooping
      // intercepts rather than a constant beeline; commit with dashes. A
      // favourable type matchup makes the AI press harder.
      const adv = this.advantage(world);
      steer = scale(aimDir, 0.5);
      const lined = range < rimRadius * 0.9;
      // Strike when in range and ready.
      dash = lined && self.dashCooldown <= 0 && this.rng() < (0.6 + 0.4 * this.difficulty) * adv;
      // Commit an extreme dash from distance with healthy spin.
      extremeDash =
        !lined &&
        self.extremeCooldown <= 0 &&
        self.spin > self.maxSpin * 0.4 &&
        this.rng() < (0.25 + 0.4 * this.difficulty) * adv;
    } else if (posture === 'defensive') {
      // Sit near center; counter by nudging into the opponent when very close.
      if (selfFromCenter > rimRadius * 0.25) {
        steer = normalize(toCenter);
        holdCenter = false;
      } else {
        steer = { x: 0, y: 0 };
        holdCenter = true;
      }
      dash = range < rimRadius * 0.4 && self.dashCooldown <= 0 && this.rng() < 0.5;
    } else {
      // Evasive: keep distance, orbit away from the opponent, hold center if far.
      if (range < rimRadius * 0.7) {
        // Flee perpendicular-ish to the incoming line.
        const away = normalize(scale(toOpp, -1));
        steer = this.aimWithNoise(away);
        holdCenter = false;
      } else {
        steer = normalize(toCenter);
        holdCenter = selfFromCenter < rimRadius * 0.3;
      }
    }

    const special = this.wantsSpecial(world, posture, range);
    return { steer, dash, extremeDash, holdCenter, special };
  }

  /** Fire the special when the meter is full and the situation suits it. */
  private wantsSpecial(world: WorldSnapshot, posture: Posture, range: number): boolean {
    const { self, rimRadius } = world;
    if (!isSpecialReady(self.special)) return false;
    let situational: boolean;
    if (posture === 'defensive') situational = range < rimRadius * 0.55; // counter when close
    else if (posture === 'evasive') situational = self.spin < self.maxSpin * 0.6; // heal when low
    else situational = range < rimRadius * 1.1; // rush/focus when opponent reachable
    return situational && this.rng() < 0.3 + 0.5 * this.difficulty;
  }

  /** Add aim error inversely proportional to difficulty. */
  private aimWithNoise(dir: Vec2): Vec2 {
    const spread = (1 - this.difficulty) * 0.6; // radians of max error
    const angle = (this.rng() * 2 - 1) * spread;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return { x: dir.x * cos - dir.y * sin, y: dir.x * sin + dir.y * cos };
  }
}
